import {Component, DestroyRef, inject, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    FormArray,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import {of} from 'rxjs';
import {concatAll, filter, map} from 'rxjs/operators';
import {AttributeGroupService} from '@emm/core/services/attribute-group.service';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SelectValue} from '@emm/core/models/select-value.model';
import {RegexConstants} from '@emm/core/util/regex.constants';
import { Tag } from '@emm/core/models/tag.model';
import {AttributeService} from '@emm/core/services/attribute.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {TagService} from '@emm/core/services/tag.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-add-attribute-dialog',
    styleUrls: ['./add-attribute-dialog.component.scss'],
    templateUrl: './add-attribute-dialog.component.html',
    standalone: false
})
export class AddAttributeDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    attributeGroups: AttributeGroup[];
    attributeType: string;
    attributeTypes;
    allSelectValues: SelectValue[] = [];
    defaultRequiredTypes = [
        {tag: '', value: ''},
        {tag: 'true', value: 'True'},
        {tag: 'false', value: 'False'}
    ];
    checkBoxValue = false;
    form: FormGroup;
    isReadOnly$ = this.authFacade.isReadOnly$;
    name: string;
    requiredTypes = [
        {tag: 'true', value: 'True'},
        {tag: 'false', value: 'False'}
    ];
    scopes = [
        {tag: 'GLOBAL', value: 'Global'},
        {tag: 'LOCAL', value: 'Dynamic'}
    ];
    selectForm: FormGroup;
    showDefaultForm = true;
    spaceValidatorPattern = RegexConstants.STARTING_SPACE_VALIDATIOR_PATTERN;
    tags: Tag[];
    disableSave;

    constructor(
        public attributeService: AttributeService,
        public attributeGroupService: AttributeGroupService,
        private authFacade: AuthFacadeService,
        public dialogRef: MatDialogRef<AddAttributeDialogComponent>,
        public tagService: TagService,
        private notificationService: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
    }


    ngOnInit(): void {
        this.dialogRef.updatePosition({top: '100px'});
        this.attributeGroupService.getAttributeGroups()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((attributeGroups) => this.attributeGroups = attributeGroups);
        if (this.data.createAttribute) {
            this.buildForm();
            this.attributeTypes = this.data.attributeTypes;
        } else if (this.data.updateAttribute) {
            this.showDefaultForm = false;
            this.name = this.data.attribute.name;
            this.attributeType = this.data.attribute.typetag;
            this.patchSelectValueForm();
        }
    }


    onNoClick(): void {
        this.dialogRef.close();
    }


    buildForm(): void {
        this.form = new FormGroup({
            attributeGroup: new FormControl(''),
            defaultValue: new FormControl(''),
            description: new FormControl(''),
            fromDate: new FormControl(''),
            localeSupport: new FormControl(false),
            locked: new FormControl(false),
            objectName: new FormControl(''),
            name: new FormControl(''),
            required: new FormControl(false),
            scope: new FormControl(''),
            sortWeight: new FormControl(''),
            tag: new FormControl(''),
            toDate: new FormControl(''),
            typetag: new FormControl(''),
            validationExpression: new FormControl(''),
        });
        this.form.get('typetag').valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.form.get('defaultValue').reset());
    }


    findTagExists(): void {
        if (this.form.controls['tag'].valid) {
            this.tagService.getAttributeTags(this.form.get('tag').value)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(res => {
                if (res === true) {
                    this.form.controls['tag'].setErrors({tagExists: true});
                }
            });
        }
    }


    buildSelectValForm(): void {
        this.selectForm = new FormGroup({
            selectValues: new FormArray([this.addSelectValues()])
        });
        this.name = this.form.controls['name'].value;
    }


    patchSelectValueForm(): void {
        this.selectForm = new FormGroup({
            selectValues: new FormArray([])
        });
        this.patchSelectValues();
    }


    get selectValues(): FormArray {
        return this.selectForm.get('selectValues') as FormArray;
    }


    addSelectValues(): FormGroup {
        return new FormGroup({
            checkBox: new FormControl(true),
            selectedValue: new FormControl('', Validators.required)
        });
    }


    patchSelectValues(): FormArray {
        const controlSV = this.selectValues;
        this.data.selectValues.forEach(x => {
            controlSV.push(this.patchSV(x.selectValue, x.defaultValue));
        });
        return controlSV;
    }


    patchSV(selectValue, defaultValue): FormGroup {
        return new FormGroup({
            checkBox: new FormControl(defaultValue),
            selectedValue: new FormControl(selectValue, Validators.required)
        });
    }


    addSelectValues2(): FormGroup {
        return new FormGroup({
            checkBox: new FormControl(false),
            selectedValue: new FormControl('', Validators.required)
        });
    }


    addSelectedValues(): void {
        this.selectValues.push(this.addSelectValues2());
    }


    removeSelectedValues(i): void {
        this.selectValues.removeAt(i);
    }


    valueExists(): void {
        if (this.selectValues.controls.length > 1) {
            for (let i = 0; i < this.selectValues.controls.length; i++) {
                if (
                    this.selectForm
                        .get('selectValues')
                        .get('' + i)
                        .get('selectedValue').value
                ) {
                    this.selectForm
                        .get('selectValues')
                        .get('' + i)
                        .get('selectedValue')
                        .setErrors(null);
                }
            }
            for (let i = 0; i < this.selectValues.controls.length; i++) {
                const dupSelectedValue = this.selectForm
                    .get('selectValues')
                    .get('' + i)
                    .get('selectedValue').value;
                if (dupSelectedValue) {
                    for (let j = i + 1; j < this.selectValues.controls.length; j++) {
                        if (
                            dupSelectedValue ===
                            this.selectForm
                                .get('selectValues')
                                .get('' + j)
                                .get('selectedValue').value
                        ) {
                            this.selectForm
                                .get('selectValues')
                                .get('' + j)
                                .get('selectedValue')
                                .setErrors({duplicate: true});
                            this.selectForm
                                .get('selectValues')
                                .get('' + i)
                                .get('selectedValue')
                                .setErrors({duplicate: true});
                        }
                    }
                }
            }
        }
    }


    defaultValueExists(i): void {
        if (this.attributeType === 'SELECT') {
            const defaultValue: boolean = this.selectForm
                .get('selectValues')
                .get('' + i)
                .get('checkBox').value;
            if (defaultValue) {
                this.checkBoxValue = false;
                for (let j = 0; j < this.selectValues.controls.length; j++) {
                    if (i !== j) {
                        this.selectForm
                            .get('selectValues')
                            .get('' + j)
                            .get('checkBox')
                            .patchValue(false);
                    }
                }
            } else {
                this.checkBoxValue = true;
            }
        }
    }

    OnSubmit(): void {
        this.disableSave = true;
        this.notificationService.success('Processing, please wait.')
        if (this.form.valid) {
            if (this.form.get('typetag').value === 'SELECT' || this.form.get('typetag').value === 'MULTI_SELECT') {
                this.attributeType = this.form.get('typetag').value;
                this.buildSelectValForm(); // intialize select form
                this.showDefaultForm = false;
                return;
            }
            this.createAttribute();
        }
    }


    selectValuesSubmit(): void {
        if (!this.checkBoxValue && this.selectForm.valid) {
            this.saveAllSelectValues();
            if (this.data.createAttribute) {
                this.createAttribute();
            } else {
                this.updateAttribute();
            }
        }
    }


    closeAfterSubmit(attribute): void {
        this.dialogRef.close(attribute);
    }


    showFirstForm() {
        this.showDefaultForm = true;
    }


    createAttribute() {
        const attribute = {
            attributeGroup: this.form.controls['attributeGroup'].value,
            defaultValue: '',
            description: this.form.controls['description'].value,
            jsonProperty: this.form.controls['objectName'].value,
            localeSupport: this.form.controls['localeSupport'].value,
            locked: this.form.controls['locked'].value,
            name: this.form.controls['name'].value,
            options: '',
            required: this.form.controls['required'].value,
            scope: this.form.controls['scope'].value,
            sortWeight: this.form.controls['sortWeight'].value,
            tag: this.form.controls['tag'].value,
            typetag: this.form.controls['typetag'].value,
        };
        if (this.showDefaultForm) {
            attribute['defaultValue'] = this.form.controls['defaultValue'].value;
            attribute['validationExpression'] = this.form.controls['validationExpression'].value;
        } else {
            attribute['defaultValue'] = this.findDefaultSelectValue();
            attribute['options'] = this.findAllSelectValues();
        }
        const typeTag = this.form.get('typetag').value;
        if (typeTag === 'DATE' && this.form.controls['date']) {
            attribute['defaultValue'] = new Date(this.form.controls['date'].value)
                .toISOString()
                .slice(0, 10);
        }

        this.closeAfterSubmit(attribute);
    }


    updateAttribute(): void {
        const attribute = {
            ...this.data.attribute,
            options: this.findAllSelectValues()
        };

        if (attribute.typetag === 'SELECT') {
            attribute.defaultValue = attribute.options.find((o) => o.defaultValue).selectValue;
        }

        this.attributeService.updateAttribute(attribute)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res) => {
            this.dialogRef.close(res);
        });
    }


    saveAllSelectValues(): void {
        of(this.selectValues.value)
            .pipe(
                concatAll(),
                map((selectValue) => this.convertListValue(selectValue)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res: any) => {
                this.allSelectValues.push(res);
            });
    }


    findAllSelectValues(): string {
        let optionValues: string;
        of(this.selectValues.value)
            .pipe(
                concatAll(),
                map((selectValue: any) => selectValue.selectedValue),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res: any) => {
                optionValues = optionValues !== undefined ? optionValues + ',' + res : res;
            });
        return optionValues.trim();
    }


    findDefaultSelectValue(): string {
        let defaultValue: string;
        of(this.selectValues.value)
            .pipe(
                concatAll(),
                filter((selectValue: any) => selectValue.checkBox),
                map((selectValue: any) => selectValue.selectedValue),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res) => {
                defaultValue = defaultValue !== undefined ? defaultValue + ',' + res : res;
            });
        return defaultValue;
    }


    numberValidation(): void {
        if (this.form.controls['typetag'].value === 'NUM' && this.form.controls['defaultValue'].value) {
            const pattern = new RegExp('^[0-9]+(.[0-9]{1,2})?$');
            if (pattern.test(this.form.controls['defaultValue'].value)) {
                this.form.controls['defaultValue'].setErrors(null);
            } else {
                this.form.controls['defaultValue'].setErrors({pattern: true});
            }
        }
    }


    validationExpressionValidation(): void {
        const validationExpression = this.form.controls['validationExpression'].value;
        const defaultValueControl = this.form.controls['defaultValue'];
        const isMatch = new RegExp(validationExpression).test(defaultValueControl.value);
        if (!validationExpression || !defaultValueControl.value || isMatch) {
            defaultValueControl.setErrors(null);
            return;
        }
        defaultValueControl.setErrors({pattern: true});
    }


    imageValidation(): void {
        const defaultValueControl = this.form.controls['defaultValue'];
        const pattern = new RegExp(
            '^https?:\\/\\/' +                              // Match http:// or https://
            '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
            '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
            '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
            '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
            '(\\?.*)?(#.*)?$',                              // Optional query and fragment
            'i'                                             // Case-insensitive
        );

        if (!defaultValueControl.value || pattern.test(defaultValueControl.value)) {
            defaultValueControl.setErrors(null);
        } else {
            defaultValueControl.setErrors({pattern: true});
        }
    }


    convertListValue(selectValue): {} {
        const listValue = {};
        if (this.data.updateAttribute) {
            listValue['tag'] = this.data.attribute.tag;
        } else {
            listValue['label'] = this.form.controls['tag'].value;
        }
        listValue['value'] = selectValue.selectedValue;
        return listValue;
    }


    dateRangeUpdated($event): void {
        if ($event.target.value === '') {
            this.form.get('defaultValue').setValue(null);
        }
    }


}
